import {
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import type { PackActionProposal } from '@quipex/shared/contracts';
import {
    QpxButton,
    QpxDropSurface,
    QpxIcon,
    QpxTooltip,
    injectPopover,
} from '@quipex/shared/ui';
import { QuippyPackAssignedDocument } from '../assigned-document/assigned-document';
import { FocusRestoration } from '../focus-restoration';
import { ProposalState } from '../proposal-state';
import type { DocumentAssignment } from './assignment-popover/assignment-popover';
import { DocumentDragState } from './document-drag-state';
@Component({
    selector: 'qpx-quippy-pack-document',
    imports: [
        CdkDragHandle,
        CdkDragPlaceholder,
        MatButtonModule,
        NgIcon,
        QpxButton,
        QpxDropSurface,
        QpxIcon,
        QpxTooltip,
        QuippyPackAssignedDocument,
    ],
    templateUrl: './pack-document.html',
    styleUrl: './pack-document.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'document-control inline-flex w-fit min-w-0 max-w-full items-center',
    },
    hostDirectives: [CdkDrag],
})
export class QuippyPackDocument {
    private readonly popover = injectPopover();
    private readonly focus = inject(FocusRestoration);
    private readonly documentDrag = inject(DocumentDragState);
    protected readonly draft = inject(ProposalState);
    public readonly actionId = input.required<string>();
    public readonly document =
        input.required<PackActionProposal['documents'][number]>();
    public readonly busy = input(false);
    public readonly selected = input(false);
    public readonly sectionName = input<string | null>(null);
    public readonly requirementId = input('');
    public readonly changed = output<void>();
    public readonly moved = output<string>();
    constructor() {
        const drag = inject(CdkDrag);
        effect(() => {
            drag.data = this.document().documentId;
            drag.disabled = this.busy();
            drag.dragStartDelay = { touch: 180, mouse: 0 };
        });
        drag.started.pipe(takeUntilDestroyed()).subscribe(() => {
            const container = drag.dropContainer;
            this.documentDrag.source.set({
                id: container.id,
                height: container.element.nativeElement.getBoundingClientRect()
                    .height,
            });
            this.draft.draggingDocument.set(true);
        });
        drag.ended.pipe(takeUntilDestroyed()).subscribe(() => {
            this.documentDrag.source.set(undefined);
            this.draft.draggingDocument.set(false);
        });
    }
    protected remove(): void {
        if (this.busy()) return;
        this.draft.selectDocument(this.document().documentId, false);
        this.changed.emit();
        this.focus.afterRender(
            `${this.actionId()}-requirement-${this.requirementId()}`
        );
    }
    protected assign(event: Event): void {
        const origin = event.currentTarget;
        if (!(origin instanceof HTMLElement)) return;
        if (this.busy()) return;
        this.popover.open({
            origin,
            closeOnOutsidePointer: true,
            loadComponent: async () =>
                (await import('./assignment-popover/assignment-popover'))
                    .QuippyPackAssignmentPopover,
            providers: [{ provide: ProposalState, useValue: this.draft }],
            componentInputs: {
                documentName: this.document().title,
                documentId: this.document().documentId,
                sectionKey:
                    this.draft
                        .groups()
                        .find((group) =>
                            group.rows.some((row) =>
                                row.documents.some(
                                    (doc) =>
                                        doc.documentId ===
                                        this.document().documentId
                                )
                            )
                        )?.key ??
                    this.draft
                        .groups()
                        .find(
                            (group) => group.name === this.draft.sectionName()
                        )?.key ??
                    '',
                section: this.sectionName() ?? this.draft.sectionName(),
                requirement: this.requirementId(),
                applyAssignment: (assignment: DocumentAssignment) => {
                    if (this.busy()) return;
                    const moved = this.draft.moveDocument(
                        this.document().documentId,
                        assignment.requirement,
                        assignment.section,
                        assignment.sectionKey
                    );
                    if (!moved) return;
                    const destination = this.draft
                        .groups()
                        .find((group) =>
                            group.rows.some((row) =>
                                row.documents.some(
                                    (doc) =>
                                        doc.documentId ===
                                        this.document().documentId
                                )
                            )
                        );
                    if (destination) this.moved.emit(destination.key);
                    this.changed.emit();
                    const id =
                        this.draft
                            .groups()
                            .flatMap((group) => group.rows)
                            .find((row) =>
                                row.documents.some(
                                    (doc) =>
                                        doc.documentId ===
                                        this.document().documentId
                                )
                            )?.id ?? this.document().documentId;
                    this.focus.afterRender(
                        `${this.actionId()}-requirement-${id}`
                    );
                },
            },
        });
    }
}
